import { z } from 'zod';
import type { WhatsappShareStatus, WhatsappShareTrigger } from './enums';

/**
 * Whether a visit's reports can be sent on WhatsApp, and if not, why.
 *
 * **Derived, never stored.** It is recomputed from the patient, the reports and
 * the server's configuration on every read, so there is no Prisma enum and no
 * `enum-parity.ts` line — those assert that a Zod enum matches a database one,
 * and this has no database one to match.
 *
 * Exactly one reason is reported: the first thing standing in the way, chosen so
 * the operator is told something they can act on. See `shareReadiness` on the
 * server for the order and why it is that order.
 */
export type ShareReadiness =
  /** No WHATSAPP_* configuration on this server. Nothing the front desk can fix. */
  | 'not_configured'
  /** No phone number on the visit at all. */
  | 'no_number'
  /** A number, but not one WhatsApp reaches — a landline, or a typo. */
  | 'invalid_number'
  /** Nobody has recorded that the patient agreed. */
  | 'no_consent'
  /** No report on this visit is completed, so there is nothing to send. */
  | 'not_finalized'
  /** A send for this visit is already running. */
  | 'in_flight'
  | 'ready';

/** What the patient screen needs to render the Share button and its dialog. */
export interface ShareState {
  readiness: ShareReadiness;
  /** E.164 with a `+`, for a human to check before a report leaves the building. */
  recipient: string | null;
  consent_at: string | null;
  /** Finalized reports, in the order they appear in the PDF. */
  included: { id: number; name: string }[];
  /** Ordered but not finalized — named so the dialog can say what is left out. */
  excluded: { id: number; name: string }[];
}

/** Asking for a visit's finalized reports to be sent. */
export const shareRequestSchema = z.object({
  /**
   * The finalized report ids the operator saw in the dialog.
   *
   * The stale-screen guard: if another test was released while the dialog was
   * open, they agreed to send something other than what would actually go.
   */
  report_ids: z.array(z.number().int().positive()).min(1, 'There is nothing finalized to send'),
  /**
   * Send even though an attempt for this exact set already exists.
   *
   * Named for what it guards rather than for what the operator thinks they are
   * doing: the Retry on a failed attempt and the Send again on a successful one
   * both set it, because from the server's side they are the same act — a new
   * attempt row.
   */
  confirm_resend: z.boolean().default(false),
});
export type ShareRequestInput = z.infer<typeof shareRequestSchema>;

/** One row of the audit log. */
export interface ShareRow {
  id: number;
  patient_id: number | null;
  patient_ref: string;
  report_ids: number[];
  report_names: string;
  report_total_at_send: number;
  attempt_no: number;
  retry_of_id: number | null;
  /** Exactly what was dialled. Never re-derived — the patient's number can move. */
  to_e164: string;
  to_raw: string;
  status: WhatsappShareStatus;
  wamid: string | null;
  media_id: string | null;
  template_name: string;
  template_language: string;
  document_filename: string;
  document_sha256: string;
  document_bytes: number;
  transport_attempts: number;
  error_code: string | null;
  error_title: string | null;
  /** Written for the front desk. Never a Graph error code. */
  error_message: string | null;
  /** Meta's trace id — the first thing their support asks for. */
  fbtrace_id: string | null;
  retryable: boolean;
  consent_at: string;
  created_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  failed_at: string | null;
  updated_at: string;
  trigger: WhatsappShareTrigger;
  /** Who pressed Share, or who released the last test of an automatic send. */
  shared_by_id: number;
  shared_by: { id: number; first_name: string; last_name: string | null };
}
